/**
 * Reading files off a paste or a drop.
 *
 * Both arrive as a `DataTransfer`, and both need the same two answers: which of
 * what it carries are files, and what to call them. A screenshot on the
 * clipboard is not a file anybody named — the browser hands over a blob called
 * `image.png` at best and nothing at all at worst — and the name is what the
 * composer shows and what the server guesses a content type from when the file
 * is later downloaded, so it is derived here rather than trusted.
 *
 * What differs between the two routes is one rule, and it has a name below:
 * a paste can be rich text that merely happens to carry a picture, a drop
 * cannot.
 */

export interface TransferMedia {
  file: File;
  /** Always carries an extension, which is what makes the kind knowable. */
  name: string;
}

/**
 * Extensions for what a clipboard or a drag realistically holds.
 *
 * Deliberately short: anything missing falls through to the media type's own
 * subtype, which is right far more often than it is wrong (`image/bmp` →
 * `.bmp`), and a name the interface cannot classify is shown as a document —
 * honest, and still sendable.
 */
const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/svg+xml': 'svg',
  'video/quicktime': 'mov',
  'video/x-matroska': 'mkv',
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/x-wav': 'wav',
  'text/plain': 'txt',
};

/** `image/png;charset=binary` is one media type with a parameter, not another type. */
function extensionFor(mimeType: string): string {
  const [base] = mimeType.split(';');
  const type = (base ?? '').trim().toLowerCase();
  if (!type) return '';
  const known = EXTENSIONS[type];
  if (known) return known;
  const subtype = type.split('/')[1] ?? '';
  return /^[a-z0-9]{1,8}$/.test(subtype) ? subtype : '';
}

/**
 * The name to attach a transferred file under.
 *
 * A file dragged from a file manager or copied there arrives with its real
 * name, and that name is the one worth keeping. A blob does not, so it gets a
 * timestamp — the same shape a recorded voice message uses, which is what keeps
 * two of them in one conversation distinguishable.
 */
export function transferMediaName(file: File, at: number = Date.now()): string {
  const given = file.name.trim();
  if (/\.[a-z0-9]{1,8}$/i.test(given)) return given;
  const extension = extensionFor(file.type);
  return extension ? `attachment-${at}.${extension}` : `attachment-${at}`;
}

/**
 * Whether a transfer offers files at all.
 *
 * Asked before anything is shown: dragging selected text or a link across the
 * conversation must not promise to attach something that was never a file.
 */
export function carriesFiles(data: DataTransfer | null | undefined): boolean {
  if (!data) return false;
  return Array.from(data.types).includes('Files');
}

/**
 * Whether a paste is rich text that merely happens to carry a picture.
 *
 * Copying a range of spreadsheet cells puts three things on the clipboard: the
 * text, the HTML, and an image of the cells. Treating that as a file would
 * replace pasting a table with pasting a screenshot of one. Copying an image
 * from a web page looks different — HTML and the file, with no text — and
 * copying a file in a file manager carries no HTML at all, so both still
 * attach.
 */
function isRichTextPaste(data: DataTransfer): boolean {
  const types = Array.from(data.types);
  if (!types.includes('text/plain') || !types.includes('text/html')) return false;
  return data.getData('text/plain').trim().length > 0;
}

function named(data: DataTransfer): TransferMedia[] {
  return Array.from(data.files).map((file) => ({ file, name: transferMediaName(file) }));
}

/**
 * The files a paste offers, in the order the clipboard listed them.
 *
 * Empty means the paste is not about files and must be left to the browser: a
 * handler that swallowed every paste would break typing.
 */
export function clipboardMedia(data: DataTransfer | null | undefined): TransferMedia[] {
  if (!data) return [];
  if (data.files.length === 0) return [];
  if (isRichTextPaste(data)) return [];
  return named(data);
}

/**
 * The files a drop offers.
 *
 * No rich-text rule here, and that asymmetry is deliberate: dragging text or a
 * link produces no file at all, so a drop that carries one carries it because
 * that is the thing being dragged.
 */
export function droppedMedia(data: DataTransfer | null | undefined): TransferMedia[] {
  if (!data) return [];
  return named(data);
}
